// Shared debounce helpers for search inputs.
//
// This wraps the existing `debounce` npm package (already a project dependency, and already
// used correctly in a few places such as BatchModal's `searchInvoices`/`searchBatch` and
// DeliveryModal's `searchLocation`) rather than introducing a second debouncing mechanism.
// It replaces the various hand-rolled `if (this.xTimeout) clearTimeout(...); this.xTimeout =
// setTimeout(...)` implementations that were duplicated (with inconsistent delays and
// min-length guards, and in at least one case a broken empty-query guard) across several
// search inputs.
import debounce from 'debounce';

// Standard debounce delay used across search inputs in the app.
export const DEFAULT_DEBOUNCE_MS = 500;
// Standard minimum query length before a search fires - avoids firing a network request on
// a 1-2 character query.
export const DEFAULT_MIN_QUERY_LENGTH = 3;

/**
 * Wraps a search callback so it only runs `delay` ms after the caller stops invoking it, and
 * only when the query is at least `minLength` characters. Shorter/empty queries are silently
 * skipped (matching how prescriber/GP/user searches already behave), rather than firing a
 * request per keystroke.
 *
 * Declare it in `data()` and assign it in `created()`, NOT in `methods:`:
 *   data() { return { debounceGpSearch: null, ... } },
 *   created() { this.debounceGpSearch = debounceSearch((query) => this.gpSearch(query)); },
 *
 * The debounced function carries `.clear()` and `.flush()` as own properties, and templates call
 * `debounceGpSearch.clear()` on Enter to fire the search immediately. Vue 3 rebinds everything in
 * `methods:` to the component instance, and `Function.prototype.bind` returns a fresh function
 * without those properties - so a declaration in `methods:` throws
 * "debounceGpSearch.clear is not a function" the first time a user presses Enter. Assigning in
 * `created()` leaves the instance untouched. Use an arrow function for the callback so `this` is
 * the component; `debounceSearch` invokes it with the wrapper as `this`.
 */
export function debounceSearch(fn, { delay = DEFAULT_DEBOUNCE_MS, minLength = DEFAULT_MIN_QUERY_LENGTH } = {}) {
    return debounce(function (query, ...rest) {
        if (!query || query.length < minLength) return;
        fn.call(this, query, ...rest);
    }, delay);
}

// Re-exported for call sites that need to debounce a callback which isn't itself a
// query-string search (e.g. re-running a fixed lookup after the user stops typing), so every
// debounced method in the app can still import from one place.
export { default as debounce } from 'debounce';
